import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'

import { CreateLink, Link } from '../types/link'
import {
  loadLinksTree,
  loadLinksTreeById,
  modifyLink,
  removeLink,
  saveLink,
} from '../api/link'
import { RootState } from '../../../../redux/store'
import { sortLinks } from '../utils/sortLinks'

interface GetLinkById {
  linkTree: Link[]
}

//get linksTree
export const getLinksTree = createAsyncThunk(
  'links/getLinksTree',
  async (_, thunkApi) => {
    try {
      return await loadLinksTree()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//get linksTreeById
export const getLinksTreeById = createAsyncThunk(
  'links/getLinksTreeById',
  async ({ id }: { id: string }, thunkApi) => {
    try {
      const linkTree = await loadLinksTreeById(id)
      return { linkTree: linkTree?.children || [] }
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//goBackToParentFolder
export const goBackToParentFolder = createAsyncThunk(
  'links/goBackToParentFolder',
  async (_, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState

      let linksParentsIds = state.link.linksParentsIds
      let linkTree: Link[] = []
      if (linksParentsIds[linksParentsIds.length - 2] === 'root') {
        linkTree = await loadLinksTree()
      } else {
        linkTree =
          (await loadLinksTreeById(linksParentsIds[linksParentsIds.length - 2]))
            ?.children || []
      }

      return linkTree
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//create link
export const addLink = createAsyncThunk(
  'link/addLink',
  async (link: CreateLink, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState
      let linksParentsIds: string[] = state.link.linksParentsIds
      return linksParentsIds.length === 1
        ? await saveLink(link)
        : (await saveLink(link)).children
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//update link
export const updateLink = createAsyncThunk(
  'link/updateLink',
  async (link: Link, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState
      let linksParentsIds: string[] = state.link.linksParentsIds
      return linksParentsIds.length === 1
        ? await modifyLink(link)
        : (await modifyLink(link)).children
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//delete link
export const deleteLink = createAsyncThunk(
  'link/deleteLink',
  async (id: string, thunkApi) => {
    try {
      return await removeLink(id)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface authSliceState {
  linksTree: Link[]
  linksParentsIds: string[]
  loading: boolean
  activeListFolderId?: string
  error?: SerializedError
}

const initialState: authSliceState = {
  linksTree: [],
  linksParentsIds: ['root'],
  loading: true,
  activeListFolderId: undefined,
  error: undefined,
}

const linkSlice = createSlice({
  name: 'link',
  initialState,
  reducers: {
    setLinksParentsIds: (state, action: PayloadAction<string[]>) => {
      state.linksParentsIds = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLinksTree.fulfilled, (state, action: PayloadAction<Link[]>) => {
      if (state.activeListFolderId !== undefined) {
        return
      }

      state.linksTree = sortLinks(action.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getLinksTree.pending, (state) => {
      state.loading = true
      state.linksTree = []
      state.activeListFolderId = undefined
      state.error = undefined
    })
    builder.addCase(getLinksTree.rejected, (state, action) => {
      if (state.activeListFolderId !== undefined) {
        return
      }

      state.loading = false
      state.linksTree = []
      state.error = action.error
    })
    builder.addCase(getLinksTreeById.fulfilled, (
      state,
      action: PayloadAction<GetLinkById> & {
        meta: { arg: { id: string } }
      },
    ) => {
      if (state.activeListFolderId !== action.meta.arg.id) {
        return
      }

      state.linksTree = sortLinks(action.payload.linkTree)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getLinksTreeById.pending, (state, action) => {
      state.loading = true
      state.linksTree = []
      state.activeListFolderId = action.meta.arg.id
      state.error = undefined
    })
    builder.addCase(getLinksTreeById.rejected, (state, action) => {
      if (state.activeListFolderId !== action.meta.arg.id) {
        return
      }

      state.loading = false
      state.linksTree = []
      state.error = action.error
    })
    builder.addCase(goBackToParentFolder.fulfilled, (
      state,
      action: PayloadAction<Link[]>,
    ) => {
      state.linksTree = sortLinks(action.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(goBackToParentFolder.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(goBackToParentFolder.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addLink.fulfilled, (state, action: PayloadAction<Link[]>) => {
      state.linksTree = sortLinks(action?.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addLink.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addLink.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateLink.fulfilled, (state, action: PayloadAction<Link[]>) => {
      state.linksTree = sortLinks(action.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateLink.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateLink.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteLink.fulfilled, (state, action: PayloadAction<string>) => {
      state.linksTree = state.linksTree.filter(
        (link) => link.id !== action.payload,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteLink.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteLink.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export const { setLinksParentsIds } = linkSlice.actions
export default linkSlice.reducer
